// src/cockpit4/components/CostAnalyzer.tsx
// ============================================
// 成本分析与优化 - 财务中心 P1-功能6
//
// 功能：
//   1. 顶部总成本摘要（总成本 / 同比 / 环比）+ 时间范围切换（月/季/年）
//   2. 左侧成本结构饼图（按类别：工资/房租/营销/采购/差旅/税费/其他）
//   3. 右侧成本趋势折线图（按月度展示各类别成本变化）
//   4. 中部成本优化机会清单（内置规则引擎识别，卡片式，优先级排序，含潜在节省金额）
//   5. 底部 ROI 分析（各项目/产品/渠道投入产出比，柱状图 + 表格）
//   6. 行业 benchmark 对比表（内置 SaaS 行业基准，对比给出建议）
//   7. 降本增效方案（基于优化机会生成的具体建议清单）
//
// 数据来源（白皮书铁律一：禁止假数据）：
//   1. 支出交易聚合：localStorage(key: xeaos_finance_transactions)
//      读取 type=expense 的交易，按 category 聚合为各类成本
//   2. 成本科目：localStorage(key: xeaos_finance_statements)
//      读取 costOfGoodsSold/marketingExpenses/adminExpenses/taxExpenses/operatingExpenses
//      映射到成本类别，作为交易数据的补充（仅填补无交易记录的类别，避免重复计算）
//   3. ROI 收入：RuntimeManager.query('revenue_engine' as any, { type: 'revenue_by_product' })
//      对比用户录入的投入金额，计算 ROI = (收入 - 投入) / 投入
//   4. ROI 投入金额：localStorage(key: xeaos_finance_roi_investments) 人工录入
//   无数据时显示"暂无成本数据 · 请先录入收支明细"，绝不展示假数字。
//
// 规则引擎（内置，非外部 AI）：
//   - 营销成本占比 > 30% → "营销成本过高，建议优化投放策略"
//   - 房租成本占比 > 25% → "房租占比偏高，可考虑共享办公"
//   - 工资成本占比 > 50% → "人力成本较高，考虑自动化"
//   - 单类成本环比增长 > 20% → "XX成本环比增长异常"
//   - 超出行业 benchmark 健康区间 → benchmark_gap 机会
//
// 权限：财务中心仅超级管理员开放（useUserStore.isSuperAdmin()），非超管显示权限提示。
// ============================================

import { useState, useEffect, useCallback, useMemo } from "react";
import type { ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pie,
  Cell,
  PieChart,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Wallet,
  PieChart as PieIcon,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Lightbulb,
  Target,
  BarChart3,
  Scale,
  Database,
  Lock,
  Plus,
  X,
  Trash2,
  Loader2,
  CheckCircle2,
  Calendar,
  PiggyBank,
  ArrowUpRight,
  ArrowDownRight,
  Settings,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { useUserStore } from "../../stores/userStore";

// ============================================
// 类型定义（与规格对齐）
// ============================================

/** 成本类别 */
type CostCategory = "salary" | "rent" | "marketing" | "procurement" | "travel" | "tax" | "other";

/** 成本项（按类别聚合） */
interface CostItem {
  category: CostCategory;
  amount: number;
  percentage: number; // 占比（0-1）
  trend: "up" | "down" | "stable";
  monthOverMonth: number; // 环比变化（-1~1）
}

/** 优化机会 */
interface OptimizationOpportunity {
  id: string;
  category: CostCategory;
  type: "high_ratio" | "abnormal_growth" | "benchmark_gap";
  title: string;
  detail: string;
  potentialSaving: number; // 潜在节省金额
  priority: "high" | "medium" | "low";
  suggestion: string; // 具体建议
}

/** ROI 项 */
interface ROIItem {
  name: string; // 项目/产品/渠道名
  investment: number; // 投入
  revenue: number; // 产出（收入）
  roi: number; // ROI = (收入-投入)/投入
  status: "profit" | "loss" | "break_even";
}

/** ROI 投入录入项（人工录入，持久化到 localStorage） */
interface ROIInvestment {
  id: string;
  name: string; // 项目/产品/渠道名
  investment: number; // 投入金额（元）
  createdAt: string;
}

/** 从 localStorage 读取的交易流水（防御性结构，兼容多种形态） */
interface FinanceTransaction {
  id?: string;
  amount?: number; // 金额（正数）
  direction?: "income" | "expense"; // 收支方向
  type?: string; // 交易类型（兼容 TransactionList 的 type 字段）
  category?: string; // 类别字符串
  date?: string; // 交易日期 ISO
  value?: number; // 金额带符号（负=支出）
  sign?: number; // 符号（负=支出）
  counterparty?: string;
  note?: string;
}

/** 财务三表数据（从 xeaos_finance_statements 读取，仅取成本相关字段） */
interface StatementData {
  revenue?: number;
  costOfGoodsSold?: number; // 营业成本 → procurement
  operatingExpenses?: number; // 运营费用 → other
  marketingExpenses?: number; // 营销费用 → marketing
  adminExpenses?: number; // 管理费用 → salary
  taxExpenses?: number; // 税费 → tax
}

/** 持久化的财务三表结构 */
interface PersistedFinance {
  current: StatementData;
  previousMonth?: StatementData | null;
}

/** 趋势时间范围 */
type PeriodRange = "month" | "quarter" | "year";

// ============================================
// 常量配置
// ============================================

const TRANSACTIONS_KEY = "xeaos_finance_transactions";
const STATEMENTS_KEY = "xeaos_finance_statements";
const ROI_KEY = "xeaos_finance_roi_investments";

/** 所有成本类别（顺序固定） */
const ALL_CATEGORIES: CostCategory[] = [
  "salary",
  "rent",
  "marketing",
  "procurement",
  "travel",
  "tax",
  "other",
];

/** 类别配置：中文标签 + 颜色 + 关键词（用于交易类别模糊匹配） */
const CATEGORY_META: Record<CostCategory, { label: string; color: string; keywords: string[] }> = {
  salary: { label: "工资", color: "#60A5FA", keywords: ["salary", "工资", "薪资", "薪酬", "人工", "工费"] },
  rent: { label: "房租", color: "#A78BFA", keywords: ["rent", "房租", "租金", "场地", "物业", "租赁"] },
  marketing: { label: "营销", color: "#F472B6", keywords: ["marketing", "营销", "推广", "广告", "市场", "投放", "宣传"] },
  procurement: { label: "采购", color: "#34D399", keywords: ["procurement", "采购", "进货", "物料", "原料", "供应链"] },
  travel: { label: "差旅", color: "#FBBF24", keywords: ["travel", "差旅", "出差", "报销", "交通", "住宿", "机票"] },
  tax: { label: "税费", color: "#F87171", keywords: ["tax", "税", "税费", "增值税", "所得税", "附加税"] },
  other: { label: "其他", color: "#94A3B8", keywords: ["other", "其他", "杂项", "办公", "水电", "杂费"] },
};

/** 行业 benchmark（内置 SaaS 行业基准，占比为百分比 0-100） */
const BENCHMARKS: Record<CostCategory, { label: string; healthyRange: [number, number]; avgRange: number }> = {
  salary: { label: "工资", healthyRange: [30, 45], avgRange: 38 },
  rent: { label: "房租", healthyRange: [5, 15], avgRange: 10 },
  marketing: { label: "营销", healthyRange: [15, 25], avgRange: 20 },
  procurement: { label: "采购", healthyRange: [10, 20], avgRange: 15 },
  travel: { label: "差旅", healthyRange: [2, 5], avgRange: 3 },
  tax: { label: "税费", healthyRange: [5, 10], avgRange: 7 },
  other: { label: "其他", healthyRange: [3, 8], avgRange: 5 },
};

/** 优化规则阈值（占比为百分比 0-100） */
const RULE_THRESHOLDS = {
  marketingRatio: 30, // 营销占比告警阈值
  rentRatio: 25, // 房租占比告警阈值
  salaryRatio: 50, // 工资占比告警阈值
  momGrowth: 20, // 环比增长告警阈值（百分比）
};

/** 时间范围选项 */
const PERIOD_OPTIONS: { id: PeriodRange; label: string }[] = [
  { id: "month", label: "月度" },
  { id: "quarter", label: "季度" },
  { id: "year", label: "年度" },
];

// ============================================
// 工具函数
// ============================================

/** 格式化金额（元 → 万元/元），防御 undefined/NaN */
function formatMoney(amount: number): string {
  const num = Number(amount);
  if (!Number.isFinite(num)) return "¥ 0";
  if (Math.abs(num) >= 10000) {
    return `¥ ${(num / 10000).toFixed(2)} 万`;
  }
  return `¥ ${num.toLocaleString()}`;
}

/** 格式化百分比（0-1 → x%） */
function formatPercent(ratio: number): string {
  const num = Number(ratio);
  if (!Number.isFinite(num)) return "0%";
  return `${(num * 100).toFixed(1)}%`;
}

/** 格式化百分比（直接传 0-100 数值） */
function formatPercentRaw(value: number): string {
  const num = Number(value);
  if (!Number.isFinite(num)) return "0%";
  return `${num.toFixed(1)}%`;
}

/** 生成唯一 ID */
function genId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** 获取当前季度（1-4） */
function getCurrentQuarter(): number {
  return Math.ceil((new Date().getMonth() + 1) / 3);
}

// ============================================
// localStorage 读取（防御性解析）
// ============================================

/** 读取交易流水（兼容直接数组 / { transactions: [] } 两种形态） */
function loadTransactions(): FinanceTransaction[] {
  try {
    const raw = localStorage.getItem(TRANSACTIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    const list: unknown = Array.isArray(parsed) ? parsed : parsed?.transactions;
    if (!Array.isArray(list)) return [];
    return list as FinanceTransaction[];
  } catch {
    return [];
  }
}

/** 读取财务三表（仅取成本相关字段） */
function loadStatements(): PersistedFinance | null {
  try {
    const raw = localStorage.getItem(STATEMENTS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedFinance;
    if (!parsed?.current) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** 读取 ROI 投入录入项 */
function loadROIInvestments(): ROIInvestment[] {
  try {
    const raw = localStorage.getItem(ROI_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as ROIInvestment[];
  } catch {
    return [];
  }
}

/** 写入 ROI 投入录入项 */
function saveROIInvestments(list: ROIInvestment[]): void {
  try {
    localStorage.setItem(ROI_KEY, JSON.stringify(list));
  } catch {
    // 写入失败不阻塞主流程
  }
}

// ============================================
// 交易 → 成本类别 聚合工具
// ============================================

/** 判断交易是否为支出（兼容 direction/type/sign/value 多种形态） */
function isExpenseTransaction(tx: FinanceTransaction): boolean {
  if (tx.direction) return tx.direction === "expense";
  if (tx.type) return tx.type === "expense";
  if (typeof tx.sign === "number") return tx.sign < 0;
  if (typeof tx.value === "number") return tx.value < 0;
  // 默认按支出处理（无方向标识时）
  return true;
}

/** 提取交易金额（正数） */
function extractAmount(tx: FinanceTransaction): number {
  if (typeof tx.amount === "number" && tx.amount > 0) return tx.amount;
  if (typeof tx.value === "number") return Math.abs(tx.value);
  return 0;
}

/** 将交易 category 字符串映射到成本类别（关键词模糊匹配，未命中归 other） */
function matchTransactionCategory(txCategory: string | undefined): CostCategory {
  if (!txCategory) return "other";
  const lower = String(txCategory).toLowerCase();
  for (const cat of ALL_CATEGORIES) {
    const meta = CATEGORY_META[cat];
    if (meta.keywords.some((kw) => lower.includes(kw.toLowerCase()))) {
      return cat;
    }
  }
  return "other";
}

/**
 * 将财务三表字段映射到成本类别
 * - costOfGoodsSold（营业成本）→ procurement
 * - marketingExpenses（营销费用）→ marketing
 * - adminExpenses（管理费用）→ salary（管理费用通常含人力）
 * - taxExpenses（税费）→ tax
 * - operatingExpenses（运营费用）→ other
 */
function mapStatementToCosts(stmt: StatementData): Record<CostCategory, number> {
  const result = {} as Record<CostCategory, number>;
  for (const cat of ALL_CATEGORIES) result[cat] = 0;
  if (!stmt) return result;
  result.procurement += Number(stmt.costOfGoodsSold) || 0;
  result.marketing += Number(stmt.marketingExpenses) || 0;
  result.salary += Number(stmt.adminExpenses) || 0;
  result.tax += Number(stmt.taxExpenses) || 0;
  result.other += Number(stmt.operatingExpenses) || 0;
  return result;
}

/**
 * 判断交易日期是否落在指定周期内
 * @param dateStr 交易日期 ISO
 * @param period 周期类型
 * @param year 目标年份
 * @param month 目标月份（monthly/quarterly 时使用）
 * @param quarter 目标季度（quarterly 时使用）
 */
function isDateInPeriod(
  dateStr: string | undefined,
  period: PeriodRange,
  year: number,
  month?: number,
  quarter?: number
): boolean {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  if (d.getFullYear() !== year) return false;
  if (period === "month") {
    return d.getMonth() + 1 === month;
  }
  if (period === "quarter") {
    const txQuarter = Math.ceil((d.getMonth() + 1) / 3);
    return txQuarter === quarter;
  }
  // year: 仅匹配年份
  return true;
}

/**
 * 聚合指定周期内的成本（按类别）
 * 数据来源：交易流水（主）+ 财务三表成本科目（补充，仅填补无交易的类别，避免重复计算）
 * @returns Record<category, amount>
 */
function aggregateCostByCategory(
  transactions: FinanceTransaction[],
  stmt: StatementData | null,
  period: PeriodRange,
  year: number,
  month?: number,
  quarter?: number
): Record<CostCategory, number> {
  const txCost = {} as Record<CostCategory, number>;
  for (const cat of ALL_CATEGORIES) txCost[cat] = 0;

  // 1. 聚合交易流水
  for (const tx of transactions) {
    if (!isExpenseTransaction(tx)) continue;
    const amount = extractAmount(tx);
    if (amount <= 0) continue;
    if (!isDateInPeriod(tx.date, period, year, month, quarter)) continue;
    const cat = matchTransactionCategory(tx.category);
    txCost[cat] += amount;
  }

  // 2. 财务三表补充（仅填补无交易的类别，避免重复计算）
  if (stmt) {
    const stmtCost = mapStatementToCosts(stmt);
    for (const cat of ALL_CATEGORIES) {
      if (txCost[cat] === 0 && stmtCost[cat] > 0) {
        txCost[cat] = stmtCost[cat];
      }
    }
  }

  // 保留两位小数避免浮点误差
  for (const cat of ALL_CATEGORIES) {
    txCost[cat] = Math.round(txCost[cat] * 100) / 100;
  }
  return txCost;
}

/**
 * 构建成本项列表（含占比 / 趋势 / 环比）
 * @param current 当前周期各类成本
 * @param previous 上一周期各类成本
 */
function buildCostItems(
  current: Record<CostCategory, number>,
  previous: Record<CostCategory, number>
): { items: CostItem[]; total: number } {
  const total = ALL_CATEGORIES.reduce((sum, cat) => sum + current[cat], 0);
  const items: CostItem[] = ALL_CATEGORIES.map((cat) => {
    const amount = current[cat];
    const percentage = total > 0 ? amount / total : 0;
    const prevAmount = previous[cat] ?? 0;
    const monthOverMonth = prevAmount > 0 ? (amount - prevAmount) / prevAmount : 0;
    let trend: "up" | "down" | "stable" = "stable";
    if (monthOverMonth > 0.02) trend = "up";
    else if (monthOverMonth < -0.02) trend = "down";
    return { category: cat, amount, percentage, trend, monthOverMonth };
  });
  return { items, total };
}

/**
 * 构建月度趋势数据（用于折线图，按月展示各类别成本）
 * 返回最近 N 个月，每月一个数据点，包含各类别金额
 */
function buildMonthlyTrend(
  transactions: FinanceTransaction[],
  months: number = 6
): Array<{ month: string } & Record<string, number | string>> {
  const now = new Date();
  const result: Array<{ month: string } & Record<string, number | string>> = [];
  for (let i = months - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const year = d.getFullYear();
    const month = d.getMonth() + 1;
    const monthKey = `${year}-${String(month).padStart(2, "0")}`;
    const point: { month: string } & Record<string, number | string> = { month: monthKey };
    for (const cat of ALL_CATEGORIES) {
      point[cat] = 0;
    }
    for (const tx of transactions) {
      if (!isExpenseTransaction(tx)) continue;
      const amount = extractAmount(tx);
      if (amount <= 0) continue;
      if (!tx.date) continue;
      const txDate = new Date(tx.date);
      if (isNaN(txDate.getTime())) continue;
      if (txDate.getFullYear() !== year || txDate.getMonth() + 1 !== month) continue;
      const cat = matchTransactionCategory(tx.category);
      point[cat] = (point[cat] as number) + amount;
    }
    // 保留两位小数
    for (const cat of ALL_CATEGORIES) {
      point[cat] = Math.round((point[cat] as number) * 100) / 100;
    }
    result.push(point);
  }
  return result;
}

// ============================================
// 规则引擎：成本优化机会识别（内置，非外部 AI）
// ============================================

/**
 * 识别成本优化机会
 * 规则：
 *   1. 营销占比 > 30% → high_ratio
 *   2. 房租占比 > 25% → high_ratio
 *   3. 工资占比 > 50% → high_ratio
 *   4. 单类环比增长 > 20% → abnormal_growth
 *   5. 超出 benchmark 健康区间上限 → benchmark_gap
 */
function detectOpportunities(
  items: CostItem[],
  total: number
): OptimizationOpportunity[] {
  const opportunities: OptimizationOpportunity[] = [];
  if (total <= 0) return opportunities;

  for (const item of items) {
    const ratioPercent = item.percentage * 100;
    const meta = CATEGORY_META[item.category];
    const benchmark = BENCHMARKS[item.category];

    // 规则 1-3：高占比告警（按类别阈值）
    if (item.category === "marketing" && ratioPercent > RULE_THRESHOLDS.marketingRatio) {
      const excess = ratioPercent - RULE_THRESHOLDS.marketingRatio;
      opportunities.push({
        id: genId("opp"),
        category: item.category,
        type: "high_ratio",
        title: "营销成本过高，建议优化投放策略",
        detail: `营销成本占比 ${ratioPercent.toFixed(1)}%，超过 ${RULE_THRESHOLDS.marketingRatio}% 告警线，金额 ${formatMoney(item.amount)}`,
        potentialSaving: Math.round(((excess / 100) * total) * 100) / 100,
        priority: "high",
        suggestion: "优化广告投放渠道，聚焦高 ROAS 渠道；削减低效曝光，A/B 测试素材；将预算向转化率最高的渠道倾斜。",
      });
    }
    if (item.category === "rent" && ratioPercent > RULE_THRESHOLDS.rentRatio) {
      const excess = ratioPercent - RULE_THRESHOLDS.rentRatio;
      opportunities.push({
        id: genId("opp"),
        category: item.category,
        type: "high_ratio",
        title: "房租占比偏高，可考虑共享办公",
        detail: `房租成本占比 ${ratioPercent.toFixed(1)}%，超过 ${RULE_THRESHOLDS.rentRatio}% 告警线，金额 ${formatMoney(item.amount)}`,
        potentialSaving: Math.round(((excess / 100) * total) * 100) / 100,
        priority: "medium",
        suggestion: "评估共享办公或混合办公模式；协商续租降租；将部分工位转为远程办公以缩减面积。",
      });
    }
    if (item.category === "salary" && ratioPercent > RULE_THRESHOLDS.salaryRatio) {
      const excess = ratioPercent - RULE_THRESHOLDS.salaryRatio;
      opportunities.push({
        id: genId("opp"),
        category: item.category,
        type: "high_ratio",
        title: "人力成本较高，考虑自动化",
        detail: `工资成本占比 ${ratioPercent.toFixed(1)}%，超过 ${RULE_THRESHOLDS.salaryRatio}% 告警线，金额 ${formatMoney(item.amount)}`,
        potentialSaving: Math.round(((excess / 100) * total) * 100) / 100,
        priority: "high",
        suggestion: "引入 AI 自动化流程替代重复性人力工作；优化组织架构，提升人效；将非核心岗位外包或转为兼职。",
      });
    }

    // 规则 4：环比增长异常
    if (item.monthOverMonth > RULE_THRESHOLDS.momGrowth / 100) {
      const growthAmount = item.amount - item.amount / (1 + item.monthOverMonth);
      opportunities.push({
        id: genId("opp"),
        category: item.category,
        type: "abnormal_growth",
        title: `${meta.label}成本环比增长异常`,
        detail: `${meta.label}成本环比增长 ${(item.monthOverMonth * 100).toFixed(1)}%，超过 ${RULE_THRESHOLDS.momGrowth}% 阈值，金额 ${formatMoney(item.amount)}`,
        potentialSaving: Math.round(growthAmount * 100) / 100,
        priority: "medium",
        suggestion: `核查 ${meta.label} 支出明细，定位增长来源；建立审批流程控制该类别支出；与历史均值对比排查异常项。`,
      });
    }

    // 规则 5：超出 benchmark 健康区间上限
    if (ratioPercent > benchmark.healthyRange[1] && item.amount > 0) {
      const excess = ratioPercent - benchmark.healthyRange[1];
      opportunities.push({
        id: genId("opp"),
        category: item.category,
        type: "benchmark_gap",
        title: `${meta.label}成本超出行业基准`,
        detail: `${meta.label}成本占比 ${ratioPercent.toFixed(1)}%，高于行业健康区间上限 ${benchmark.healthyRange[1]}%（行业均值 ${benchmark.avgRange}%）`,
        potentialSaving: Math.round(((excess / 100) * total) * 100) / 100,
        priority: ratioPercent > benchmark.healthyRange[1] * 1.3 ? "high" : "low",
        suggestion: `对标行业均值 ${benchmark.avgRange}%，制定降本目标；参考同业最佳实践优化 ${meta.label} 支出结构。`,
      });
    }
  }

  // 按优先级排序：high > medium > low；同优先级按潜在节省金额降序
  const priorityWeight: Record<OptimizationOpportunity["priority"], number> = { high: 3, medium: 2, low: 1 };
  opportunities.sort((a, b) => {
    const pw = priorityWeight[b.priority] - priorityWeight[a.priority];
    if (pw !== 0) return pw;
    return b.potentialSaving - a.potentialSaving;
  });

  return opportunities;
}

/**
 * 基于优化机会生成降本增效建议清单
 * 汇总各机会的建议，去重并按优先级分组
 */
function buildSuggestions(opportunities: OptimizationOpportunity[]): Array<{
  id: string;
  title: string;
  detail: string;
  priority: OptimizationOpportunity["priority"];
  saving: number;
}> {
  return opportunities.map((opp) => ({
    id: opp.id,
    title: opp.suggestion.split("；")[0] || opp.title,
    detail: opp.suggestion,
    priority: opp.priority,
    saving: opp.potentialSaving,
  }));
}

/**
 * 计算 benchmark 对比数据
 * 返回各类别：实际占比 / 健康区间 / 行业均值 / 状态（healthy/warning/danger/none）
 */
function buildBenchmarkComparison(
  items: CostItem[]
): Array<{
  category: CostCategory;
  label: string;
  actualPercent: number;
  healthyRange: [number, number];
  avgRange: number;
  status: "healthy" | "warning" | "danger" | "none";
  advice: string;
}> {
  return items
    .filter((item) => item.amount > 0 || item.category === "other")
    .map((item) => {
      const benchmark = BENCHMARKS[item.category];
      const meta = CATEGORY_META[item.category];
      const actualPercent = item.percentage * 100;
      let status: "healthy" | "warning" | "danger" | "none" = "none";
      let advice = "";
      if (item.amount <= 0) {
        status = "none";
        advice = "暂无该类别成本数据";
      } else if (actualPercent < benchmark.healthyRange[0]) {
        status = "healthy";
        advice = `低于健康区间下限，投入偏低，可酌情增加以支撑业务`;
      } else if (actualPercent <= benchmark.healthyRange[1]) {
        status = "healthy";
        advice = `处于行业健康区间，结构合理`;
      } else if (actualPercent <= benchmark.healthyRange[1] * 1.3) {
        status = "warning";
        advice = `略高于健康区间上限，建议关注并优化`;
      } else {
        status = "danger";
        advice = `显著超出健康区间，需立即优化降本`;
      }
      return {
        category: item.category,
        label: meta.label,
        actualPercent,
        healthyRange: benchmark.healthyRange,
        avgRange: benchmark.avgRange,
        status,
        advice,
      };
    });
}

// ============================================
// RevenueEngine 解包工具（双层包裹，参考 FinanceDashboard）
// ============================================

interface RevenueByProductResp {
  date: string;
  products: Array<{
    productId: string;
    productName: string;
    todayAmount: number;
    monthAmount: number;
    yearAmount: number;
    customerCount: number;
    share: number;
    growth: number;
  }>;
}

/** 解包 RuntimeManager.query 的双层返回，取出真实数据对象 */
function unwrapRevenue(resp: any): RevenueByProductResp | null {
  if (!resp?.success) return null;
  const inner = resp.data;
  if (inner && typeof inner === "object" && "data" in inner) {
    return ((inner as { data: unknown }).data as RevenueByProductResp) ?? null;
  }
  return (inner as RevenueByProductResp) ?? null;
}

/**
 * 构建 ROI 分析数据
 * 投入 = 用户录入（ROIInvestment），产出 = RevenueEngine 按产品匹配收入
 * ROI = (收入 - 投入) / 投入
 */
function buildROIItems(
  investments: ROIInvestment[],
  revenueByProduct: RevenueByProductResp | null
): ROIItem[] {
  if (!investments.length) return [];
  const products = revenueByProduct?.products ?? [];
  return investments.map((inv) => {
    const name = inv.name.trim();
    // 按产品名匹配收入（忽略大小写包含匹配）
    const matched = products.find(
      (p) =>
        p.productName &&
        (p.productName.toLowerCase() === name.toLowerCase() ||
          p.productName.toLowerCase().includes(name.toLowerCase()) ||
          name.toLowerCase().includes(p.productName.toLowerCase()))
    );
    const revenue = matched ? (matched.yearAmount || matched.monthAmount || 0) : 0;
    const roi = inv.investment > 0 ? (revenue - inv.investment) / inv.investment : 0;
    let status: ROIItem["status"] = "break_even";
    if (roi > 0.05) status = "profit";
    else if (roi < -0.05) status = "loss";
    return { name, investment: inv.investment, revenue, roi, status };
  });
}

// ============================================
// 主组件
// ============================================

export default function CostAnalyzer() {
  // 权限：财务中心仅超级管理员开放
  const isSuperAdmin = useUserStore((s) => s.isSuperAdmin());

  const [transactions, setTransactions] = useState<FinanceTransaction[]>([]);
  const [statements, setStatements] = useState<PersistedFinance | null>(null);
  const [roiInvestments, setRoiInvestments] = useState<ROIInvestment[]>([]);
  const [revenueByProduct, setRevenueByProduct] = useState<RevenueByProductResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [lastUpdated, setLastUpdated] = useState<string>("");
  const [showROIForm, setShowROIForm] = useState(false);

  // 时间范围
  const now = new Date();
  const [period, setPeriod] = useState<PeriodRange>("month");
  const [year] = useState(now.getFullYear());
  const [month] = useState(now.getMonth() + 1);
  const [quarter] = useState(getCurrentQuarter());

  // ============================================
  // 加载数据
  // ============================================
  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");
    // 1. 读取 localStorage（交易流水 + 财务三表 + ROI 投入）
    const txList = loadTransactions();
    const stmt = loadStatements();
    const roiList = loadROIInvestments();
    setTransactions(txList);
    setStatements(stmt);
    setRoiInvestments(roiList);

    // 2. 拉取 RevenueEngine 真实收入数据（用于 ROI 计算）
    try {
      let ready = false;
      for (let i = 0; i < 30; i++) {
        if (RuntimeManager.isInitialized()) {
          ready = true;
          break;
        }
        await new Promise((r) => setTimeout(r, 300));
      }
      if (ready && RuntimeManager.hasRuntime("revenue_engine" as any)) {
        const resp = await RuntimeManager.query("revenue_engine" as any, { type: "revenue_by_product" });
        const data = unwrapRevenue(resp);
        if (data) setRevenueByProduct(data);
      }
    } catch (err: any) {
      // RevenueEngine 拉取失败不阻塞成本分析主流程
      console.warn("[CostAnalyzer] RevenueEngine 拉取失败:", err?.message);
    }

    setLastUpdated(new Date().toLocaleString("zh-CN", { hour12: false }));
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  // ============================================
  // 派生计算：当前周期成本 + 上一周期成本（用于环比/同比）
  // ============================================

  /** 上一周期参数（用于环比计算） */
  const prevPeriodParams = useMemo(() => {
    if (period === "month") {
      const d = new Date(year, month - 1, 1);
      return { period: "month" as PeriodRange, year: d.getFullYear(), month: d.getMonth() + 1, quarter: undefined };
    }
    if (period === "quarter") {
      const prevQ = quarter - 1;
      if (prevQ < 1) {
        return { period: "quarter" as PeriodRange, year: year - 1, month: undefined, quarter: 4 };
      }
      return { period: "quarter" as PeriodRange, year, month: undefined, quarter: prevQ };
    }
    return { period: "year" as PeriodRange, year: year - 1, month: undefined, quarter: undefined };
  }, [period, year, month, quarter]);

  /** 同期去年参数（用于同比计算） */
  const yoyPeriodParams = useMemo(() => {
    if (period === "month") {
      return { period: "month" as PeriodRange, year: year - 1, month, quarter: undefined };
    }
    if (period === "quarter") {
      return { period: "quarter" as PeriodRange, year: year - 1, month: undefined, quarter };
    }
    return { period: "year" as PeriodRange, year: year - 1, month: undefined, quarter: undefined };
  }, [period, year, month, quarter]);

  const currentCostMap = useMemo(
    () => aggregateCostByCategory(transactions, statements?.current ?? null, period, year, month, quarter),
    [transactions, statements, period, year, month, quarter]
  );

  const prevCostMap = useMemo(
    () => aggregateCostByCategory(transactions, statements?.previousMonth ?? null, prevPeriodParams.period, prevPeriodParams.year, prevPeriodParams.month, prevPeriodParams.quarter),
    [transactions, statements, prevPeriodParams]
  );

  const yoyCostMap = useMemo(
    () => aggregateCostByCategory(transactions, null, yoyPeriodParams.period, yoyPeriodParams.year, yoyPeriodParams.month, yoyPeriodParams.quarter),
    [transactions, yoyPeriodParams]
  );

  /** 当前周期成本项（含占比/趋势/环比） */
  const { items: costItems, total: totalCost } = useMemo(
    () => buildCostItems(currentCostMap, prevCostMap),
    [currentCostMap, prevCostMap]
  );

  /** 上一周期总成本 */
  const prevTotalCost = useMemo(
    () => ALL_CATEGORIES.reduce((sum, cat) => sum + prevCostMap[cat], 0),
    [prevCostMap]
  );

  /** 同期去年总成本 */
  const yoyTotalCost = useMemo(
    () => ALL_CATEGORIES.reduce((sum, cat) => sum + yoyCostMap[cat], 0),
    [yoyCostMap]
  );

  // 环比 = (当前 - 上期) / 上期
  const momChange = prevTotalCost > 0 ? (totalCost - prevTotalCost) / prevTotalCost : 0;
  // 同比 = (当前 - 同期去年) / 同期去年
  const yoyChange = yoyTotalCost > 0 ? (totalCost - yoyTotalCost) / yoyTotalCost : null;

  /** 是否有成本数据 */
  const hasCostData = totalCost > 0;

  // ============================================
  // 派生：成本结构饼图数据 / 趋势折线图数据
  // ============================================
  const pieData = useMemo(
    () =>
      costItems
        .filter((item) => item.amount > 0)
        .map((item) => ({ name: CATEGORY_META[item.category].label, value: item.amount, category: item.category })),
    [costItems]
  );

  const trendData = useMemo(
    () => buildMonthlyTrend(transactions, 6),
    [transactions]
  );

  // ============================================
  // 派生：优化机会 / benchmark 对比 / 降本建议
  // ============================================
  const opportunities = useMemo(
    () => detectOpportunities(costItems, totalCost),
    [costItems, totalCost]
  );

  const benchmarkComparison = useMemo(
    () => buildBenchmarkComparison(costItems),
    [costItems]
  );

  const suggestions = useMemo(
    () => buildSuggestions(opportunities),
    [opportunities]
  );

  /** 潜在总节省金额 */
  const totalPotentialSaving = useMemo(
    () => opportunities.reduce((sum, o) => sum + o.potentialSaving, 0),
    [opportunities]
  );

  // ============================================
  // 派生：ROI 分析
  // ============================================
  const roiItems = useMemo(
    () => buildROIItems(roiInvestments, revenueByProduct),
    [roiInvestments, revenueByProduct]
  );

  // ============================================
  // ROI 投入录入表单
  // ============================================
  const [roiForm, setRoiForm] = useState({ name: "", investment: "" });

  const handleAddROI = useCallback(() => {
    const name = roiForm.name.trim();
    const investment = Number(roiForm.investment) || 0;
    if (!name || investment <= 0) return;
    const newItem: ROIInvestment = {
      id: genId("roi"),
      name,
      investment,
      createdAt: new Date().toISOString(),
    };
    const next = [...roiInvestments, newItem];
    saveROIInvestments(next);
    setRoiInvestments(next);
    setRoiForm({ name: "", investment: "" });
  }, [roiForm, roiInvestments]);

  const handleRemoveROI = useCallback((id: string) => {
    const next = roiInvestments.filter((r) => r.id !== id);
    saveROIInvestments(next);
    setRoiInvestments(next);
  }, [roiInvestments]);

  // ============================================
  // 渲染：权限校验（非超管显示权限提示）
  // ============================================
  if (!isSuperAdmin) {
    return (
      <div className="h-full w-full overflow-hidden p-4">
        <div className="h-full flex flex-col items-center justify-center rounded-xl border border-red-500/20 bg-red-500/[0.02] backdrop-blur-xl">
          <div className="w-16 h-16 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mb-4">
            <Lock size={28} className="text-red-400" />
          </div>
          <h2 className="text-[16px] font-semibold text-white mb-2">权限受限</h2>
          <p className="text-[12px] font-mono text-gray-400 text-center max-w-sm leading-relaxed">
            成本分析与优化仅对超级管理员开放。
            <br />
            普通员工无权查看财务数据。
          </p>
          <p className="text-[10px] font-mono text-gray-600 mt-4">
            如需访问，请联系超级管理员授权。
          </p>
        </div>
      </div>
    );
  }

  // ============================================
  // 渲染：主面板
  // ============================================
  return (
    <div className="h-full w-full overflow-hidden p-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="h-full flex flex-col rounded-xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl overflow-hidden"
      >
        {/* ============ Header ============ */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-pink-500/15 border border-pink-500/30 flex items-center justify-center">
              <PiggyBank size={16} className="text-pink-400" />
            </div>
            <div>
              <h2 className="text-[15px] font-semibold text-white tracking-tight">成本分析与优化</h2>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                Cost Analyzer · 结构 · 趋势 · 优化机会 · ROI · Benchmark
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-[9px] font-mono text-gray-600 hidden md:inline">{lastUpdated}</span>
            )}
            <button
              onClick={() => setShowROIForm(true)}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-white/[0.08] bg-white/[0.03] text-gray-300 hover:bg-white/[0.06] transition"
            >
              <Settings size={10} />
              ROI 投入
            </button>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-pink-500/30 bg-pink-500/10 text-pink-300 hover:bg-pink-500/20 transition disabled:opacity-50"
            >
              {loading ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
              {loading ? "加载中" : "刷新"}
            </button>
          </div>
        </div>

        {/* ============ 内容区 ============ */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {error && (
            <div className="px-4 py-2 rounded-md border border-red-500/20 bg-red-500/[0.03] text-[10px] font-mono text-red-400">
              <AlertCircle size={11} className="inline mr-1" />
              {error}
            </div>
          )}

          {/* ============ 顶部：总成本摘要 + 时间范围切换 ============ */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <Wallet size={12} className="text-pink-400" />
                <h3 className="text-[12px] font-medium text-white">总成本摘要</h3>
                <span className="text-[9px] font-mono text-gray-500">
                  （{period === "month" ? `${year}年${month}月` : period === "quarter" ? `${year}年 Q${quarter}` : `${year}年`}）
                </span>
              </div>
              {/* 时间范围切换 */}
              <div className="flex items-center gap-0.5 p-0.5 rounded-md border border-white/[0.06] bg-white/[0.02]">
                {PERIOD_OPTIONS.map((opt) => (
                  <button
                    key={opt.id}
                    onClick={() => setPeriod(opt.id)}
                    className={`px-2.5 py-1 rounded text-[10px] font-mono transition ${
                      period === opt.id
                        ? "bg-pink-500/20 text-pink-300"
                        : "text-gray-500 hover:text-gray-300 hover:bg-white/[0.04]"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {hasCostData ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* 总成本 */}
                <SummaryCard
                  icon={<Wallet size={14} className="text-pink-400" />}
                  label="总成本"
                  enLabel="Total Cost"
                  value={formatMoney(totalCost)}
                  color="#F472B6"
                  sublabel={`共 ${transactions.filter((t) => isExpenseTransaction(t) && isDateInPeriod(t.date, period, year, month, quarter)).length} 笔支出`}
                />
                {/* 同比 */}
                <SummaryCard
                  icon={yoyChange !== null && yoyChange > 0 ? <ArrowUpRight size={14} className="text-red-400" /> : yoyChange !== null ? <ArrowDownRight size={14} className="text-emerald-400" /> : <Calendar size={14} className="text-gray-400" />}
                  label="同比"
                  enLabel="YoY"
                  value={yoyChange !== null ? formatPercent(yoyChange) : "—"}
                  color={yoyChange === null ? "#6B7280" : yoyChange > 0 ? "#F87171" : "#34D399"}
                  sublabel={yoyChange !== null ? `去年同期 ${formatMoney(yoyTotalCost)}` : "暂无去年同期数据"}
                />
                {/* 环比 */}
                <SummaryCard
                  icon={momChange > 0 ? <ArrowUpRight size={14} className="text-red-400" /> : <ArrowDownRight size={14} className="text-emerald-400" />}
                  label="环比"
                  enLabel="MoM"
                  value={prevTotalCost > 0 ? formatPercent(momChange) : "—"}
                  color={prevTotalCost === 0 ? "#6B7280" : momChange > 0 ? "#F87171" : "#34D399"}
                  sublabel={prevTotalCost > 0 ? `上期 ${formatMoney(prevTotalCost)}` : "暂无上期数据"}
                />
              </div>
            ) : (
              <div className="py-8 flex flex-col items-center justify-center text-center">
                <Database size={24} className="text-gray-700 mb-2" />
                <p className="text-[12px] font-mono text-gray-500">暂无成本数据 · 请先录入收支明细</p>
                <p className="text-[10px] font-mono text-gray-600 mt-1">
                  在「收支明细」中录入支出交易，或在「财务三表」中录入成本科目
                </p>
              </div>
            )}
          </div>

          {hasCostData && (
            <>
              {/* ============ 左侧饼图 + 右侧折线图 ============ */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* 成本结构饼图 */}
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <PieIcon size={12} className="text-purple-400" />
                    <h3 className="text-[12px] font-medium text-white">成本结构分析</h3>
                    <span className="text-[9px] font-mono text-gray-500">（按类别金额与占比）</span>
                  </div>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={50}
                          outerRadius={90}
                          paddingAngle={2}
                          dataKey="value"
                          nameKey="name"
                        >
                          {pieData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={CATEGORY_META[entry.category as CostCategory].color}
                              stroke="rgba(10,14,26,0.8)"
                              strokeWidth={1}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            background: "rgba(10,14,26,0.95)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontFamily: "monospace",
                          }}
                          labelStyle={{ color: "#E5E7EB" }}
                          itemStyle={{ color: "#9CA3AF" }}
                          formatter={(value: any, name: any, props: any) => {
                            const pct = totalCost > 0 ? (Number(value) / totalCost) * 100 : 0;
                            return [`${formatMoney(Number(value))} · ${pct.toFixed(1)}%`, String(name)];
                          }}
                        />
                        <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace" }} iconType="circle" />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* 成本趋势折线图 */}
                <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingUp size={12} className="text-cyan-400" />
                    <h3 className="text-[12px] font-medium text-white">成本趋势分析</h3>
                    <span className="text-[9px] font-mono text-gray-500">（近 6 个月各类别变化）</span>
                  </div>
                  <div className="h-[280px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                        <CartesianGrid stroke="#1e2a3f" strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="month"
                          tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                          stroke="rgba(255,255,255,0.06)"
                        />
                        <YAxis
                          tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                          stroke="rgba(255,255,255,0.06)"
                          tickFormatter={(v: any) => {
                            const n = Number(v);
                            return Math.abs(n) >= 10000 ? `${(n / 10000).toFixed(0)}万` : String(n);
                          }}
                        />
                        <Tooltip
                          contentStyle={{
                            background: "rgba(10,14,26,0.95)",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "6px",
                            fontSize: "11px",
                            fontFamily: "monospace",
                          }}
                          labelStyle={{ color: "#E5E7EB" }}
                          itemStyle={{ color: "#9CA3AF" }}
                          formatter={(value: any, name: any) => [formatMoney(Number(value)), String(name)]}
                        />
                        <Legend wrapperStyle={{ fontSize: "10px", fontFamily: "monospace" }} iconType="line" />
                        {ALL_CATEGORIES.map((cat) => (
                          <Line
                            key={cat}
                            type="monotone"
                            dataKey={cat}
                            name={CATEGORY_META[cat].label}
                            stroke={CATEGORY_META[cat].color}
                            strokeWidth={1.5}
                            dot={{ r: 2 }}
                            activeDot={{ r: 4 }}
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              {/* ============ 成本明细表（按类别） ============ */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 size={12} className="text-blue-400" />
                  <h3 className="text-[12px] font-medium text-white">成本类别明细</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px] font-mono">
                    <thead>
                      <tr className="text-gray-500 border-b border-white/[0.06]">
                        <th className="text-left py-2 px-2">类别</th>
                        <th className="text-right py-2 px-2">金额</th>
                        <th className="text-right py-2 px-2">占比</th>
                        <th className="text-right py-2 px-2">环比</th>
                        <th className="text-center py-2 px-2">趋势</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costItems
                        .filter((item) => item.amount > 0)
                        .map((item) => (
                          <tr key={item.category} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                            <td className="py-2 px-2">
                              <span
                                className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                                style={{ background: CATEGORY_META[item.category].color }}
                              />
                              <span className="text-gray-200">{CATEGORY_META[item.category].label}</span>
                            </td>
                            <td className="text-right py-2 px-2 text-gray-200">{formatMoney(item.amount)}</td>
                            <td className="text-right py-2 px-2 text-gray-400">{formatPercent(item.percentage)}</td>
                            <td className="text-right py-2 px-2">
                              {item.monthOverMonth > 0 ? (
                                <span className="text-red-400">+{formatPercent(item.monthOverMonth)}</span>
                              ) : item.monthOverMonth < 0 ? (
                                <span className="text-emerald-400">{formatPercent(item.monthOverMonth)}</span>
                              ) : (
                                <span className="text-gray-500">—</span>
                              )}
                            </td>
                            <td className="text-center py-2 px-2">
                              {item.trend === "up" ? (
                                <TrendingUp size={12} className="inline text-red-400" />
                              ) : item.trend === "down" ? (
                                <TrendingDown size={12} className="inline text-emerald-400" />
                              ) : (
                                <span className="text-gray-600">—</span>
                              )}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ============ 成本优化机会清单 ============ */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Lightbulb size={12} className="text-amber-400" />
                    <h3 className="text-[12px] font-medium text-white">成本优化机会</h3>
                    <span className="text-[9px] font-mono text-gray-500">（规则引擎识别，按优先级排序）</span>
                  </div>
                  {opportunities.length > 0 && (
                    <span className="text-[10px] font-mono text-amber-400">
                      潜在总节省 {formatMoney(totalPotentialSaving)} · {opportunities.length} 项
                    </span>
                  )}
                </div>
                {opportunities.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {opportunities.map((opp, idx) => (
                      <motion.div
                        key={opp.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.05, ease: "easeOut" }}
                        className={`relative rounded-lg border p-3 overflow-hidden ${
                          opp.priority === "high"
                            ? "border-red-500/30 bg-red-500/[0.03]"
                            : opp.priority === "medium"
                            ? "border-amber-500/30 bg-amber-500/[0.03]"
                            : "border-blue-500/20 bg-blue-500/[0.02]"
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <AlertTriangle
                              size={13}
                              className={
                                opp.priority === "high"
                                  ? "text-red-400"
                                  : opp.priority === "medium"
                                  ? "text-amber-400"
                                  : "text-blue-400"
                              }
                            />
                            <span className="text-[12px] font-medium text-white">{opp.title}</span>
                          </div>
                          <span
                            className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${
                              opp.priority === "high"
                                ? "bg-red-500/15 text-red-300"
                                : opp.priority === "medium"
                                ? "bg-amber-500/15 text-amber-300"
                                : "bg-blue-500/15 text-blue-300"
                            }`}
                          >
                            {opp.priority === "high" ? "高" : opp.priority === "medium" ? "中" : "低"}
                          </span>
                        </div>
                        <p className="text-[10px] font-mono text-gray-400 leading-relaxed mb-2">{opp.detail}</p>
                        <div className="flex items-center justify-between text-[10px] font-mono">
                          <span className="text-gray-500">
                            <span
                              className="inline-block w-2 h-2 rounded-full mr-1 align-middle"
                              style={{ background: CATEGORY_META[opp.category].color }}
                            />
                            {CATEGORY_META[opp.category].label}
                          </span>
                          <span className="text-emerald-400">潜在节省 {formatMoney(opp.potentialSaving)}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 flex flex-col items-center justify-center text-center">
                    <CheckCircle2 size={20} className="text-emerald-500/60 mb-2" />
                    <p className="text-[11px] font-mono text-gray-500">暂无优化机会 · 成本结构健康</p>
                  </div>
                )}
              </div>

              {/* ============ ROI 分析（柱状图 + 表格） ============ */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Target size={12} className="text-emerald-400" />
                    <h3 className="text-[12px] font-medium text-white">ROI 分析</h3>
                    <span className="text-[9px] font-mono text-gray-500">（投入产出比，收入来自 RevenueEngine）</span>
                  </div>
                  <button
                    onClick={() => setShowROIForm(true)}
                    className="flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 transition"
                  >
                    <Plus size={10} />
                    录入投入
                  </button>
                </div>

                {roiItems.length > 0 ? (
                  <>
                    {/* ROI 柱状图 */}
                    <div className="h-[240px] mb-4">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={roiItems.map((r) => ({ name: r.name, roi: Number((r.roi * 100).toFixed(1)), status: r.status }))}
                          margin={{ top: 8, right: 8, left: -8, bottom: 0 }}
                        >
                          <CartesianGrid stroke="#1e2a3f" strokeDasharray="3 3" vertical={false} />
                          <XAxis
                            dataKey="name"
                            tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                            stroke="rgba(255,255,255,0.06)"
                          />
                          <YAxis
                            tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "monospace" }}
                            stroke="rgba(255,255,255,0.06)"
                            tickFormatter={(v: any) => `${v}%`}
                          />
                          <Tooltip
                            contentStyle={{
                              background: "rgba(10,14,26,0.95)",
                              border: "1px solid rgba(255,255,255,0.1)",
                              borderRadius: "6px",
                              fontSize: "11px",
                              fontFamily: "monospace",
                            }}
                            labelStyle={{ color: "#E5E7EB" }}
                            itemStyle={{ color: "#9CA3AF" }}
                            formatter={(value: any) => [`${value}%`, "ROI"]}
                          />
                          <Bar dataKey="roi" radius={[3, 3, 0, 0]}>
                            {roiItems.map((r, idx) => (
                              <Cell
                                key={`roi-cell-${idx}`}
                                fill={r.status === "profit" ? "#10B981" : r.status === "loss" ? "#EF4444" : "#6B7280"}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    {/* ROI 表格 */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-[11px] font-mono">
                        <thead>
                          <tr className="text-gray-500 border-b border-white/[0.06]">
                            <th className="text-left py-2 px-2">项目/产品/渠道</th>
                            <th className="text-right py-2 px-2">投入</th>
                            <th className="text-right py-2 px-2">产出（收入）</th>
                            <th className="text-right py-2 px-2">ROI</th>
                            <th className="text-center py-2 px-2">状态</th>
                          </tr>
                        </thead>
                        <tbody>
                          {roiItems.map((r, idx) => (
                            <tr key={idx} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                              <td className="py-2 px-2 text-gray-200">{r.name}</td>
                              <td className="text-right py-2 px-2 text-gray-300">{formatMoney(r.investment)}</td>
                              <td className="text-right py-2 px-2 text-gray-300">
                                {r.revenue > 0 ? formatMoney(r.revenue) : "—"}
                              </td>
                              <td className="text-right py-2 px-2">
                                <span
                                  className={
                                    r.status === "profit"
                                      ? "text-emerald-400"
                                      : r.status === "loss"
                                      ? "text-red-400"
                                      : "text-gray-400"
                                  }
                                >
                                  {formatPercent(r.roi)}
                                </span>
                              </td>
                              <td className="text-center py-2 px-2">
                                <span
                                  className={`px-1.5 py-0.5 rounded text-[9px] ${
                                    r.status === "profit"
                                      ? "bg-emerald-500/15 text-emerald-300"
                                      : r.status === "loss"
                                      ? "bg-red-500/15 text-red-300"
                                      : "bg-gray-500/15 text-gray-300"
                                  }`}
                                >
                                  {r.status === "profit" ? "盈利" : r.status === "loss" ? "亏损" : "持平"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                ) : (
                  <div className="py-6 flex flex-col items-center justify-center text-center">
                    <Target size={20} className="text-gray-700 mb-2" />
                    <p className="text-[11px] font-mono text-gray-500">暂无 ROI 数据 · 点击「录入投入」添加项目</p>
                    <p className="text-[9px] font-mono text-gray-600 mt-1">
                      录入投入金额后，将自动对比 RevenueEngine 收入计算 ROI
                    </p>
                  </div>
                )}
              </div>

              {/* ============ 行业 benchmark 对比表 ============ */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Scale size={12} className="text-blue-400" />
                  <h3 className="text-[12px] font-medium text-white">行业 Benchmark 对比</h3>
                  <span className="text-[9px] font-mono text-gray-500">（内置 SaaS 行业基准）</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px] font-mono">
                    <thead>
                      <tr className="text-gray-500 border-b border-white/[0.06]">
                        <th className="text-left py-2 px-2">类别</th>
                        <th className="text-right py-2 px-2">实际占比</th>
                        <th className="text-right py-2 px-2">健康区间</th>
                        <th className="text-right py-2 px-2">行业均值</th>
                        <th className="text-center py-2 px-2">状态</th>
                        <th className="text-left py-2 px-2">建议</th>
                      </tr>
                    </thead>
                    <tbody>
                      {benchmarkComparison.map((row) => (
                        <tr key={row.category} className="border-b border-white/[0.03] hover:bg-white/[0.02]">
                          <td className="py-2 px-2">
                            <span
                              className="inline-block w-2 h-2 rounded-full mr-2 align-middle"
                              style={{ background: CATEGORY_META[row.category].color }}
                            />
                            <span className="text-gray-200">{row.label}</span>
                          </td>
                          <td className="text-right py-2 px-2 text-gray-200">
                            {row.actualPercent > 0 ? formatPercentRaw(row.actualPercent) : "—"}
                          </td>
                          <td className="text-right py-2 px-2 text-gray-400">
                            {row.healthyRange[0]}% - {row.healthyRange[1]}%
                          </td>
                          <td className="text-right py-2 px-2 text-gray-400">{row.avgRange}%</td>
                          <td className="text-center py-2 px-2">
                            <span
                              className={`px-1.5 py-0.5 rounded text-[9px] ${
                                row.status === "healthy"
                                  ? "bg-emerald-500/15 text-emerald-300"
                                  : row.status === "warning"
                                  ? "bg-amber-500/15 text-amber-300"
                                  : row.status === "danger"
                                  ? "bg-red-500/15 text-red-300"
                                  : "bg-gray-500/10 text-gray-500"
                              }`}
                            >
                              {row.status === "healthy"
                                ? "健康"
                                : row.status === "warning"
                                ? "关注"
                                : row.status === "danger"
                                ? "预警"
                                : "无数据"}
                            </span>
                          </td>
                          <td className="py-2 px-2 text-gray-500 text-[10px]">{row.advice}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ============ 降本增效方案 ============ */}
              <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  <h3 className="text-[12px] font-medium text-white">降本增效方案</h3>
                  <span className="text-[9px] font-mono text-gray-500">（基于优化机会生成）</span>
                </div>
                {suggestions.length > 0 ? (
                  <div className="space-y-2">
                    {suggestions.map((sug, idx) => (
                      <motion.div
                        key={sug.id}
                        initial={{ opacity: 0, x: -8 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: idx * 0.05, ease: "easeOut" }}
                        className="flex items-start gap-2.5 p-2.5 rounded-md border border-white/[0.04] bg-white/[0.01]"
                      >
                        <div
                          className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-mono ${
                            sug.priority === "high"
                              ? "bg-red-500/15 text-red-300"
                              : sug.priority === "medium"
                              ? "bg-amber-500/15 text-amber-300"
                              : "bg-blue-500/15 text-blue-300"
                          }`}
                        >
                          {idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-[11px] text-gray-200">{sug.title}</span>
                            <span className="text-[10px] font-mono text-emerald-400 flex-shrink-0 ml-2">
                              节省 {formatMoney(sug.saving)}
                            </span>
                          </div>
                          <p className="text-[10px] font-mono text-gray-500 leading-relaxed">{sug.detail}</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="py-6 flex flex-col items-center justify-center text-center">
                    <CheckCircle2 size={20} className="text-emerald-500/60 mb-2" />
                    <p className="text-[11px] font-mono text-gray-500">当前成本结构健康，暂无降本建议</p>
                  </div>
                )}
              </div>

              {/* ============ 数据来源说明 ============ */}
              <div className="px-4 py-2 rounded-md border border-blue-500/15 bg-blue-500/[0.02] text-[10px] font-mono text-blue-300/70">
                <Database size={11} className="inline mr-1" />
                数据来源：localStorage(xeaos_finance_transactions) 支出交易聚合 +
                localStorage(xeaos_finance_statements) 成本科目补充 +
                RevenueEngine(revenue_by_product) ROI 收入对比
                <span className="ml-2 text-gray-500">
                  · 当前周期 {transactions.filter((t) => isExpenseTransaction(t) && isDateInPeriod(t.date, period, year, month, quarter)).length} 笔支出
                </span>
              </div>
            </>
          )}
        </div>
      </motion.div>

      {/* ============ ROI 投入录入弹窗 ============ */}
      <AnimatePresence>
        {showROIForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowROIForm(false)}
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-md flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.95, y: 10 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md max-h-[80vh] flex flex-col rounded-xl border border-white/[0.08] bg-[#0a0e1a] overflow-hidden"
            >
              {/* 弹窗头部 */}
              <div className="flex items-center justify-between px-5 py-3 border-b border-white/[0.06]">
                <div className="flex items-center gap-2">
                  <Target size={14} className="text-emerald-400" />
                  <h3 className="text-[14px] font-semibold text-white">ROI 投入录入</h3>
                </div>
                <button
                  onClick={() => setShowROIForm(false)}
                  className="p-1 rounded-md text-gray-400 hover:text-white hover:bg-white/[0.05]"
                >
                  <X size={16} />
                </button>
              </div>

              {/* 弹窗内容 */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <p className="text-[10px] font-mono text-gray-500 leading-relaxed">
                  录入项目/产品/渠道的投入金额，系统将自动对比 RevenueEngine 收入计算 ROI。
                  产品名建议与 RevenueEngine 中的产品名一致以便匹配。
                </p>

                {/* 添加表单 */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={roiForm.name}
                    onChange={(e) => setRoiForm((f) => ({ ...f, name: e.target.value }))}
                    placeholder="项目/产品/渠道名"
                    className="flex-1 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
                  />
                  <input
                    type="number"
                    value={roiForm.investment}
                    onChange={(e) => setRoiForm((f) => ({ ...f, investment: e.target.value }))}
                    placeholder="投入金额（元）"
                    className="w-32 px-2.5 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] font-mono text-white placeholder-gray-600 focus:outline-none focus:border-emerald-500/40"
                  />
                  <button
                    onClick={handleAddROI}
                    className="px-2.5 py-1.5 rounded-md bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition"
                  >
                    <Plus size={12} />
                  </button>
                </div>

                {/* 已录入列表 */}
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {roiInvestments.length === 0 ? (
                    <div className="text-center py-4 text-[10px] font-mono text-gray-600">
                      暂无投入项，请添加
                    </div>
                  ) : (
                    roiInvestments.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between px-2.5 py-1.5 rounded-md border border-white/[0.04] bg-white/[0.01]"
                      >
                        <span className="text-[11px] text-gray-200">{r.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[11px] font-mono text-gray-300">{formatMoney(r.investment)}</span>
                          <button
                            onClick={() => handleRemoveROI(r.id)}
                            className="text-gray-500 hover:text-red-400 transition"
                          >
                            <Trash2 size={11} />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* 弹窗底部 */}
              <div className="px-5 py-3 border-t border-white/[0.06] flex items-center justify-between">
                <span className="text-[9px] font-mono text-gray-600">
                  数据保存到 localStorage（{ROI_KEY}）
                </span>
                <button
                  onClick={() => setShowROIForm(false)}
                  className="px-3 py-1.5 text-[11px] font-mono text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 hover:bg-emerald-500/25 rounded transition"
                >
                  完成
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ============================================
// 子组件：摘要卡片
// ============================================

interface SummaryCardProps {
  icon: ReactNode;
  label: string;
  enLabel: string;
  value: string;
  color: string;
  sublabel: string;
}

function SummaryCard({ icon, label, enLabel, value, color, sublabel }: SummaryCardProps) {
  return (
    <div
      className="relative rounded-lg border bg-white/[0.02] p-3 overflow-hidden"
      style={{ borderColor: "rgba(255,255,255,0.06)", borderTopColor: `${color}40`, borderTopWidth: 2 }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, transparent, ${color}, transparent)` }}
      />
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: `${color}15`, border: `1px solid ${color}30` }}
        >
          {icon}
        </div>
        <div>
          <h4 className="text-[11px] font-medium text-white">{label}</h4>
          <p className="text-[8px] font-mono text-gray-500 uppercase tracking-wider">{enLabel}</p>
        </div>
      </div>
      <div className="text-[28px] leading-none font-mono font-bold tracking-tight mb-1" style={{ color }}>
        {value}
      </div>
      <p className="text-[9px] font-mono text-gray-500">{sublabel}</p>
    </div>
  );
}
